import React, { useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";
import {
  btnPrimaryClass,
  btnSecondaryClass,
  modalOverlayClass,
} from "./dashboardStyles";

/**
 * A yes/no question in the dashboard's own look, in place of `window.confirm`,
 * which renders in the browser's chrome and font and says "localhost:3000 says".
 *
 * Render it only while the question is open. Focus lands on the cancel button,
 * so an Enter pressed out of habit backs out rather than destroying something;
 * Escape and a click outside the panel also cancel.
 */
const ConfirmDialog = ({ message, confirmLabel, cancelLabel, onConfirm, onCancel }) => {
  const messageId = useId();
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    // Return focus to whatever opened the dialog once it closes.
    const previouslyFocused = document.activeElement;
    cancelButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [onCancel]);

  return (
    <div className={modalOverlayClass} onClick={onCancel}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-describedby={messageId}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-card bg-white p-6 text-center shadow-card-hover"
      >
        <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-red-50 text-red-600">
          <i className="bi bi-exclamation-triangle text-xl" aria-hidden="true" />
        </div>

        <p id={messageId} className="m-0 text-base font-semibold text-ink-900">
          {message}
        </p>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <button
            ref={cancelButtonRef}
            type="button"
            className={`${btnSecondaryClass} sm:min-w-28`}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`${btnPrimaryClass} sm:min-w-28`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

ConfirmDialog.propTypes = {
  message: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  cancelLabel: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default ConfirmDialog;
